import { open, save } from "@tauri-apps/plugin-dialog";
import { readFile, writeFile } from "@tauri-apps/plugin-fs";
import type { ExportBytesOptions, ExportBytesResult, OpenedFile, PickOpenFileOptions } from "./types";

function basename(path: string): string {
  const parts = path.split(/[/\\]/);
  return parts.at(-1) || path;
}

function extensionOf(filename: string): string | undefined {
  const base = basename(filename);
  const dot = base.lastIndexOf(".");
  if (dot <= 0 || dot === base.length - 1) {
    return undefined;
  }
  return base.slice(dot + 1).toLowerCase();
}

function filtersFromAccept(accept?: string): Array<{ name: string; extensions: string[] }> | undefined {
  if (!accept) {
    return undefined;
  }
  const extensions = accept
    .split(",")
    .map(part => part.trim().replace(/^\./, "").toLowerCase())
    .filter(Boolean);
  if (extensions.length === 0) {
    return undefined;
  }
  return [{ name: "Documents", extensions }];
}

function filtersFromFilename(filename: string): Array<{ name: string; extensions: string[] }> | undefined {
  const extension = extensionOf(filename);
  if (!extension) {
    return undefined;
  }
  return [{ name: extension.toUpperCase(), extensions: [extension] }];
}

export async function tauriPickOpenFile(
  options: PickOpenFileOptions = {},
): Promise<OpenedFile | undefined> {
  const selected = await open({
    multiple: false,
    filters: filtersFromAccept(options.accept),
  });
  if (typeof selected !== "string" || selected.length === 0) {
    return undefined;
  }

  const bytes = await readFile(selected);
  return {
    name: basename(selected),
    bytes,
    path: selected,
  };
}

export async function tauriPickOpenFiles(
  options: PickOpenFileOptions = {},
): Promise<OpenedFile[]> {
  if (!options.multiple) {
    const file = await tauriPickOpenFile(options);
    return file ? [file] : [];
  }

  const selected = await open({
    multiple: true,
    filters: filtersFromAccept(options.accept),
  });
  if (!selected) {
    return [];
  }
  const paths = Array.isArray(selected) ? selected : [selected];
  const opened: OpenedFile[] = [];
  for (const path of paths) {
    opened.push({
      name: basename(path),
      bytes: await readFile(path),
      path,
    });
  }
  return opened;
}

export async function tauriExportBytes(
  bytes: Uint8Array,
  options: ExportBytesOptions,
): Promise<ExportBytesResult> {
  const path = options.path ?? await save({
    defaultPath: options.filename,
    filters: filtersFromFilename(options.filename),
  }) ?? undefined;

  if (!path) {
    return { method: "cancelled" };
  }

  // Contiguous copy: IPC serialization can fail on Uint8Array views.
  await writeFile(path, bytes.byteOffset === 0 && bytes.byteLength === bytes.buffer.byteLength
    ? bytes
    : bytes.slice());
  return { method: "write", path };
}

export async function tauriSaveDocumentBytes(
  bytes: Uint8Array,
  options: ExportBytesOptions,
): Promise<ExportBytesResult> {
  return tauriExportBytes(bytes, {
    ...options,
    // Prefer writing the known path; otherwise native Save dialog.
    path: options.path,
  });
}

export async function tauriSetWindowTitle(title: string): Promise<void> {
  const { getCurrentWindow } = await import("@tauri-apps/api/window");
  await getCurrentWindow().setTitle(title);
}

/** Native Ok/Cancel confirm (WKWebView's window.confirm is broken / routed incorrectly). */
export async function tauriConfirmDiscard(message: string): Promise<boolean> {
  const { confirm } = await import("@tauri-apps/plugin-dialog");
  return confirm(message, { title: "Curva", kind: "warning" });
}

/** Listen for window close; return false from `shouldClose` to cancel. */
export async function tauriOnWindowCloseRequested(
  shouldClose: () => boolean | Promise<boolean>,
): Promise<() => void> {
  const { getCurrentWindow } = await import("@tauri-apps/api/window");
  return getCurrentWindow().onCloseRequested(async event => {
    const allowed = await shouldClose();
    if (!allowed) {
      event.preventDefault();
    }
  });
}

/** Native OS file drop paths (Tauri). Caller filters / opens. */
export async function tauriOnFileDrop(
  onPaths: (paths: string[]) => void | Promise<void>,
): Promise<() => void> {
  const { getCurrentWindow } = await import("@tauri-apps/api/window");
  return getCurrentWindow().onDragDropEvent(event => {
    if (event.payload.type !== "drop") {
      return;
    }
    void onPaths(event.payload.paths);
  });
}

export async function tauriReadDroppedFile(path: string): Promise<{ name: string; bytes: Uint8Array; path: string }> {
  const bytes = await readFile(path);
  return { name: basename(path), bytes, path };
}

/** Read via Rust command — used for Finder/Explorer Open With (no fs-plugin scope). */
export async function tauriReadOpenWithFile(path: string): Promise<{ name: string; bytes: Uint8Array; path: string }> {
  const { invoke } = await import("@tauri-apps/api/core");
  const bytes = await invoke<number[]>("read_curva_file", { path });
  return { name: basename(path), bytes: Uint8Array.from(bytes), path };
}

export async function tauriTakePendingOpenPaths(): Promise<string[]> {
  const { invoke } = await import("@tauri-apps/api/core");
  return invoke<string[]>("take_pending_open_paths");
}

export async function tauriCurvaFileExists(path: string): Promise<boolean> {
  const { invoke } = await import("@tauri-apps/api/core");
  return invoke<boolean>("curva_file_exists", { path });
}

/**
 * AppConfig I/O goes through Rust commands (not plugin-fs).
 * plugin-fs ACL often denies creating/writing `$APPCONFIG` in release builds;
 * trusted app-owned files match the Open With bypass pattern.
 */

/** Read `{appConfigDir}/config.ini` (undefined if missing). */
export async function tauriLoadDesktopConfigText(): Promise<string | undefined> {
  const { invoke } = await import("@tauri-apps/api/core");
  const text = await invoke<string | null>("read_desktop_config_text");
  return text ?? undefined;
}

/** Write `{appConfigDir}/config.ini`. */
export async function tauriSaveDesktopConfigText(text: string): Promise<void> {
  const { invoke } = await import("@tauri-apps/api/core");
  await invoke("write_desktop_config_text", { text });
}

/** Read `{appConfigDir}/editor-scripts.curva` bytes (undefined if missing). */
export async function tauriLoadEditorScriptsBytes(): Promise<Uint8Array | undefined> {
  const { invoke } = await import("@tauri-apps/api/core");
  const bytes = await invoke<number[] | null>("read_editor_scripts_bytes");
  return bytes ? new Uint8Array(bytes) : undefined;
}

/** Write `{appConfigDir}/editor-scripts.curva`. */
export async function tauriSaveEditorScriptsBytes(bytes: Uint8Array): Promise<void> {
  const { invoke } = await import("@tauri-apps/api/core");
  // Contiguous copy: IPC serialization can fail on Uint8Array views.
  const payload = bytes.byteOffset === 0 && bytes.byteLength === bytes.buffer.byteLength
    ? bytes
    : bytes.slice();
  await invoke("write_editor_scripts_bytes", { bytes: payload });
}

/** Delete `{appConfigDir}/editor-scripts.curva` if present. */
export async function tauriClearEditorScriptsFile(): Promise<void> {
  const { invoke } = await import("@tauri-apps/api/core");
  await invoke("clear_editor_scripts_file");
}

/**
 * First-run seed: copy bundled `default-editor-scripts.curva` into AppConfig as
 * `editor-scripts.curva`, and return the bytes. No-op if AppConfig already has a file.
 */
export async function tauriSeedDefaultEditorScriptsFromBundled(): Promise<Uint8Array | undefined> {
  const existing = await tauriLoadEditorScriptsBytes();
  if (existing && existing.byteLength > 0) {
    return existing;
  }

  const { invoke } = await import("@tauri-apps/api/core");
  let bytes: Uint8Array | undefined;

  try {
    const bundled = await invoke<number[] | null>("read_bundled_default_editor_scripts");
    if (bundled && bundled.length > 0) {
      bytes = new Uint8Array(bundled);
    }
  } catch (error) {
    console.warn("[curva] failed to read bundled default editor scripts", error);
  }

  if (!bytes || bytes.byteLength === 0) {
    try {
      const response = await fetch("/default-editor-scripts.curva");
      if (response.ok) {
        bytes = new Uint8Array(await response.arrayBuffer());
      }
    } catch (error) {
      console.warn("[curva] failed to seed default editor scripts", error);
      return undefined;
    }
  }

  if (!bytes || bytes.byteLength === 0) {
    return undefined;
  }

  await tauriSaveEditorScriptsBytes(bytes);
  return bytes;
}

export async function tauriOnOpenCurvaPaths(
  onPaths: (paths: string[]) => void | Promise<void>,
): Promise<() => void> {
  const { listen } = await import("@tauri-apps/api/event");
  return listen<string[]>("open-curva-paths", event => {
    void onPaths(event.payload);
  });
}

/** Close the main window (fires onCloseRequested — use for Quit so dirty confirms run). */
export async function tauriRequestWindowClose(): Promise<void> {
  const { getCurrentWindow } = await import("@tauri-apps/api/window");
  await getCurrentWindow().close();
}
